import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Typography,
  message,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { adminApi } from '@/services/api';
import { apiErrorMessage } from '@/utils/apiError';
import {
  CHASE_TYPES,
  DEFAULT_SCENARIO,
  METRICS,
  OBJECTIVE_LABELS,
  OBJECTIVE_TYPES,
  derive,
  istDateOf,
  challengeWindow,
  istDayWindow,
  objectiveText,
  presetFor,
  type Challenge,
  type ChallengeInput,
  type ObjectiveType,
  type Scenario,
} from './challengeForm';

interface Props {
  open: boolean;
  /** null = new challenge */
  challenge: Challenge | null;
  /** IST date for a new challenge */
  defaultDate: string;
  onClose: () => void;
  onSaved: () => void;
}

interface FormValues extends Omit<ChallengeInput, 'startAt' | 'endAt' | 'rewardOverride'> {
  istDate: dayjs.Dayjs;
  endTime: dayjs.Dayjs;
  overrideOn: boolean;
  rewardOverride?: { coins: number; matchTokens: number };
}

const toInput = (v: FormValues): ChallengeInput => {
  const { istDate, endTime, overrideOn, rewardOverride, ...rest } = v;
  return {
    ...rest,
    ...challengeWindow(istDate.format('YYYY-MM-DD'), endTime ? endTime.toISOString() : null),
    rewardOverride: overrideOn && rewardOverride ? rewardOverride : null,
  };
};

const PLAYER_MODES = [
  { value: 'any', label: 'Any (game picks)' },
  { value: 'specific', label: 'Specific player id' },
];

/**
 * Create or edit one challenge. The window is one IST day. A published
 * challenge that players have attempted only takes a later end: the server
 * answers CHALLENGE_LOCKED for anything else.
 */
export const ChallengeEditor: React.FC<Props> = ({
  open,
  challenge,
  defaultDate,
  onClose,
  onSaved,
}) => {
  const [form] = Form.useForm<FormValues>();
  const [errors, setErrors] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const scenario = Form.useWatch('scenario', form) as Scenario | undefined;
  const objective = Form.useWatch('objective', form);
  const batterMode = Form.useWatch(['gameplay', 'batter', 'mode'], form);
  const nonStrikerMode = Form.useWatch(['gameplay', 'nonStriker', 'mode'], form);
  const bowlerMode = Form.useWatch(['gameplay', 'bowler', 'mode'], form);
  const overrideOn = Form.useWatch('overrideOn', form);
  const derived = useMemo(() => (scenario ? derive(scenario) : null), [scenario]);

  useEffect(() => {
    if (!open) return;
    setErrors(null);
    if (challenge) {
      form.setFieldsValue({
        ...challenge,
        istDate: dayjs(istDateOf(challenge.startAt)),
        endTime: dayjs(challenge.endAt),
        overrideOn: !!challenge.rewardOverride,
        rewardOverride: challenge.rewardOverride ?? { coins: 500, matchTokens: 5 },
      });
    } else {
      form.setFieldsValue({
        name: '',
        description: '',
        difficulty: 'medium',
        istDate: dayjs(defaultDate),
        endTime: dayjs(istDayWindow(defaultDate).endAt),
        rolloutPct: 90,
        scenario: DEFAULT_SCENARIO,
        objective: presetFor('FINAL_OVER', 22),
        gameplay: { batter: { mode: 'any' }, nonStriker: { mode: 'any' }, bowler: { mode: 'any' } },
        overrideOn: false,
        rewardOverride: { coins: 500, matchTokens: 5 },
      });
    }
  }, [open, challenge, defaultDate, form]);

  const applyPreset = (type: ObjectiveType) => {
    form.setFieldValue('objective', presetFor(type, derived?.runsRequired ?? null));
  };

  const syncChaseRuns = () => {
    const o = form.getFieldValue('objective');
    const d = derive(form.getFieldValue('scenario'));
    if (o && CHASE_TYPES.includes(o.type) && d.runsRequired !== null) {
      form.setFieldValue(['objective', 'conditions', 0], { metric: 'runs', min: d.runsRequired });
    }
  };

  const validate = async () => {
    const v = await form.validateFields();
    const res = await adminApi.challengerValidate(toInput(v));
    setErrors(res.data.errors);
    return res.data.errors as string[];
  };

  const save = async (publish: boolean) => {
    setBusy(true);
    try {
      const v = await form.validateFields();
      const body = toInput(v);
      let id = challenge?.id;
      if (challenge) {
        await adminApi.challengerUpdate(challenge.id, body);
      } else {
        id = (await adminApi.challengerCreate(body)).data.id;
      }
      if (publish && id) await adminApi.challengerPublish(id);
      message.success(publish ? 'Published' : 'Saved');
      onSaved();
    } catch (e) {
      const data = (e as { response?: { data?: { errors?: string[] } } }).response?.data;
      if (data?.errors) setErrors(data.errors);
      message.error(apiErrorMessage(e, 'Could not save the challenge'));
    } finally {
      setBusy(false);
    }
  };

  const locked = challenge?.status === 'published' || challenge?.status === 'archived';

  return (
    <Drawer
      title={
        challenge
          ? `Edit ${challenge.key} (v${challenge.version}, ${challenge.status})`
          : 'New daily challenge'
      }
      width={760}
      open={open}
      onClose={onClose}
      destroyOnClose
      extra={
        <Space>
          <Button
            onClick={() =>
              validate().catch((e) => message.error(apiErrorMessage(e, 'Validate failed')))
            }
          >
            Validate
          </Button>
          <Button loading={busy} onClick={() => save(false)}>
            Save
          </Button>
          {challenge?.status !== 'published' && (
            <Button type="primary" loading={busy} onClick={() => save(true)}>
              Save &amp; publish
            </Button>
          )}
        </Space>
      }
    >
      {locked && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Once players have attempted this challenge only its end time can move (set Ends to end it early; archive is refused); other edits are refused."
        />
      )}
      {errors && errors.length === 0 && (
        <Alert type="success" showIcon message="Valid" style={{ marginBottom: 16 }} />
      )}
      {errors && errors.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Fix before publishing"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          }
        />
      )}
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onValuesChange={(changed: Partial<FormValues>) => {
          // A new day moves the end to that day's end; ops can then move it.
          if (changed.istDate) {
            form.setFieldValue(
              'endTime',
              dayjs(istDayWindow(changed.istDate.format('YYYY-MM-DD')).endAt)
            );
          }
        }}
      >
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="name" label="Name" rules={[{ required: true, max: 100 }]}>
              <Input placeholder="Rescue the chase" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="istDate" label="IST day" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} allowClear={false} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="difficulty" label="Difficulty">
              <Select options={['easy', 'medium', 'hard'].map((d) => ({ value: d, label: d }))} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="endTime"
          label="Ends (your local time)"
          extra="Defaults to the end of the IST day. Move it to end the challenge early or late."
          rules={[{ required: true }]}
        >
          <DatePicker showTime style={{ width: 280 }} allowClear={false} />
        </Form.Item>
        <Form.Item
          name="description"
          label="Scenario text (shown to the player)"
          rules={[{ max: 255 }]}
        >
          <Input.TextArea
            rows={2}
            placeholder="Your team needs 22 runs in 6 balls. Rescue the chase and win the match."
          />
        </Form.Item>
        <Form.Item
          name="rolloutPct"
          label="Rollout %"
          tooltip="Stable per-player bucket. Players outside it never see Daily Challenge and are the holdout for the retention read."
        >
          <InputNumber min={0} max={100} />
        </Form.Item>

        <Divider orientation="left">Match state</Divider>
        <Row gutter={12}>
          <Col span={4}>
            <Form.Item name={['scenario', 'innings']} label="Innings">
              <Select
                options={[
                  { value: 1, label: '1st' },
                  { value: 2, label: '2nd' },
                ]}
                onChange={(v) => {
                  if (v === 1) form.setFieldValue(['scenario', 'targetScore'], null);
                }}
              />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item name={['scenario', 'startingScore']} label="Score">
              <InputNumber min={0} style={{ width: '100%' }} onBlur={syncChaseRuns} />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item name={['scenario', 'wicketsLost']} label="Wickets lost">
              <InputNumber min={0} max={9} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item name={['scenario', 'targetScore']} label="Target">
              <InputNumber
                min={1}
                style={{ width: '100%' }}
                disabled={scenario?.innings === 1}
                onBlur={syncChaseRuns}
              />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item name={['scenario', 'overNumber']} label="Over no.">
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={6}>
            <Form.Item name={['scenario', 'overs']} label="Overs">
              <InputNumber
                min={1}
                max={3}
                style={{ width: '100%' }}
                onChange={(v) => v && form.setFieldValue(['scenario', 'ballLimit'], Number(v) * 6)}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name={['scenario', 'ballLimit']} label="Ball limit">
              <InputNumber min={1} max={18} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            {derived && (
              <Descriptions size="small" column={3} style={{ marginTop: 30 }}>
                <Descriptions.Item label="Runs req.">
                  {derived.runsRequired ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Wkts left">{derived.wicketsRemaining}</Descriptions.Item>
                <Descriptions.Item label="Balls">{derived.ballsRemaining}</Descriptions.Item>
              </Descriptions>
            )}
          </Col>
        </Row>

        <Divider orientation="left">Objective</Divider>
        <Row gutter={12}>
          <Col span={14}>
            <Form.Item name={['objective', 'type']} label="Type">
              <Select
                options={OBJECTIVE_TYPES.map((t) => ({ value: t, label: OBJECTIVE_LABELS[t] }))}
                onChange={applyPreset}
              />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              name={['objective', 'maxWicketsLost']}
              label="Wickets allowed"
              tooltip="Empty = only all out fails. 0 = no wicket may fall."
            >
              <InputNumber min={0} max={9} style={{ width: '100%' }} placeholder="no limit" />
            </Form.Item>
          </Col>
        </Row>
        <Form.List name={['objective', 'conditions']}>
          {(fields, { add, remove }) => (
            <>
              {fields.map((f) => (
                <Space key={f.key} align="baseline">
                  <Form.Item name={[f.name, 'metric']}>
                    <Select
                      style={{ width: 220 }}
                      options={METRICS.map((m) => ({ value: m, label: m }))}
                    />
                  </Form.Item>
                  <Form.Item name={[f.name, 'min']}>
                    <InputNumber min={1} addonBefore="at least" />
                  </Form.Item>
                  {fields.length > 1 && <MinusCircleOutlined onClick={() => remove(f.name)} />}
                </Space>
              ))}
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => add({ metric: 'sixes', min: 1 })}
              >
                Add condition
              </Button>
            </>
          )}
        </Form.List>
        {objective && scenario && (
          <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
            Player sees: <b>{objectiveText(objective, scenario)}</b>
          </Typography.Paragraph>
        )}

        <Divider orientation="left">Players and conditions</Divider>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name={['gameplay', 'batter', 'mode']} label="Batter">
              <Select options={PLAYER_MODES} />
            </Form.Item>
            {batterMode === 'specific' && (
              <Form.Item name={['gameplay', 'batter', 'playerId']} rules={[{ required: true }]}>
                <Input placeholder="cricket player id" />
              </Form.Item>
            )}
          </Col>
          <Col span={8}>
            <Form.Item name={['gameplay', 'nonStriker', 'mode']} label="Non-striker">
              <Select options={PLAYER_MODES} />
            </Form.Item>
            {nonStrikerMode === 'specific' && (
              <Form.Item name={['gameplay', 'nonStriker', 'playerId']} rules={[{ required: true }]}>
                <Input placeholder="cricket player id" />
              </Form.Item>
            )}
          </Col>
          <Col span={8}>
            <Form.Item name={['gameplay', 'bowler', 'mode']} label="Bowler">
              <Select
                options={[
                  { value: 'any', label: 'Any (game picks)' },
                  { value: 'type', label: 'Bowler type' },
                  { value: 'specific', label: 'Specific player id' },
                ]}
              />
            </Form.Item>
            {bowlerMode === 'type' && (
              <Form.Item name={['gameplay', 'bowler', 'type']} rules={[{ required: true }]}>
                <Select options={['fast', 'medium', 'spin'].map((t) => ({ value: t, label: t }))} />
              </Form.Item>
            )}
            {bowlerMode === 'specific' && (
              <Form.Item name={['gameplay', 'bowler', 'playerId']} rules={[{ required: true }]}>
                <Input placeholder="cricket player id" />
              </Form.Item>
            )}
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name={['gameplay', 'delivery', 'type']} label="Delivery type">
              <Input placeholder="any" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={['gameplay', 'delivery', 'line']} label="Line">
              <Input placeholder="any" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={['gameplay', 'delivery', 'length']} label="Length">
              <Input placeholder="any" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name={['gameplay', 'field', 'preset']} label="Field preset">
              <Input placeholder="default" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={['gameplay', 'stadiumId']} label="Stadium id">
              <Input placeholder="default" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={['gameplay', 'featuredPlayerId']}
              label="Featured player"
              tooltip="Winners Alliance player shown on the popup and result screens (uses the player card art). Empty = generic art."
            >
              <Input placeholder="cricket player id" />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Reward</Divider>
        <Form.Item
          name="overrideOn"
          valuePropName="checked"
          label="Override the streak-day reward"
          tooltip="Off: the player gets the reward for their streak day (Settings tab). Day 3 / Day 7 crates are paid either way."
        >
          <Switch />
        </Form.Item>
        {overrideOn && (
          <Space>
            <Form.Item name={['rewardOverride', 'coins']} label="Coins">
              <InputNumber min={0} max={100000} />
            </Form.Item>
            <Form.Item name={['rewardOverride', 'matchTokens']} label="Match tokens">
              <InputNumber min={0} max={100000} />
            </Form.Item>
          </Space>
        )}
      </Form>
    </Drawer>
  );
};
